// The vocabulary of a model-view filter rule.
//
// THIS IS NOT A NEW VOCABULARY. It mirrors back/utils/quantityFilters.js -
// the one the construction tracking page already speaks - because the owner
// chose it ("or use the filter rule we have on construction tracking"). The
// server is the only evaluator: this file builds the request and reads the
// answer, and never decides what matches.
//
// Keep these lists in step with quantityFilters.js. If they drift, the server
// refuses the rule BY NAME, which is the failure we want - a rule the page
// offers and the server rejects is visible, while a rule the page silently
// drops is not.

export const TEXT_FIELDS = ['name', 'category', 'group', 'type', 'level'];
export const MEASURE_FIELDS = ['count', 'quantity', 'length', 'width', 'depth', 'height',
  'thickness', 'perimeter', 'diameter', 'radius', 'area', 'surfaceArea', 'volume', 'weight'];
export const STATUSES = ['projected', 'procured', 'being_installed', 'installed'];

export const TEXT_OPS = ['contains', 'equals', 'different', 'hasValue', 'none'];
export const NUMBER_OPS = ['more', 'less', 'equals', 'different', 'hasValue', 'none'];
export const STATUS_OPS = ['equals', 'different'];

export const GATES = [
  { value: 'AND', label: 'AND - matches every rule' },
  { value: 'OR', label: 'OR - matches any rule' },
  { value: 'NOT', label: 'NOT - matches none of these rules' },
];

// The server's own ceiling. Offering a 21st rule that the server will refuse
// is a worse experience than not offering it.
export const MAX_RULES = 20;

// Operators that take no value - asking for one would imply it is used.
export const VALUELESS_OPS = ['hasValue', 'none'];

export function fieldKind(field) {
  if (TEXT_FIELDS.includes(field)) return 'text';
  if (field === 'status') return 'status';
  if (MEASURE_FIELDS.includes(field)) return 'number';
  return null;
}

export function opsFor(field) {
  const kind = fieldKind(field);
  if (kind === 'text') return TEXT_OPS;
  if (kind === 'status') return STATUS_OPS;
  if (kind === 'number') return NUMBER_OPS;
  return [];
}

export const blankRule = () => ({ field: 'category', op: 'contains', value: '' });

/**
 * Is this rule complete enough to send? Returns a REASON, not a boolean,
 * so the bar can say which rule is unfinished and why.
 */
export function ruleProblem(rule, index) {
  const where = `Rule ${index + 1}`;
  if (!rule || !rule.field) return `${where}: choose a field`;
  const kind = fieldKind(rule.field);
  if (!kind) return `${where}: "${rule.field}" cannot be filtered`;
  if (!opsFor(rule.field).includes(rule.op)) return `${where}: "${rule.op}" does not apply to ${rule.field}`;
  if (VALUELESS_OPS.includes(rule.op)) return null;
  const v = rule.value;
  if (v === '' || v === null || v === undefined) return `${where}: give ${rule.field} a value`;
  if (kind === 'number' && Number.isNaN(Number(v))) return `${where}: ${rule.field} takes a number`;
  return null;
}

export function rulesProblem(rules) {
  if (!Array.isArray(rules) || !rules.length) return 'Add a rule first';
  if (rules.length > MAX_RULES) return `At most ${MAX_RULES} rules at once`;
  for (let i = 0; i < rules.length; i += 1) {
    const p = ruleProblem(rules[i], i);
    if (p) return p;
  }
  return null;
}

/**
 * The body POST /quantities/filter-uids expects.
 *
 * scope:false IS DELIBERATE AND IT IS THE WHOLE POINT OF FILTERING A VIEWER.
 * That endpoint belongs to construction tracking, and by default it narrows
 * every answer to the project's TrackingScope - which is right on the tracking
 * page, where the scope IS the subject. In the model viewer the person is
 * looking at the whole model, so a scoped answer is wrong twice over: it hides
 * elements that are on the screen in front of them, and it does so silently,
 * reporting "nothing matched" for a filter that matches.
 *
 * Measured on staging (spaceX-test, commit 6a915fe6): "category contains Cond"
 * returns 0 scoped and 10 unscoped, and the 10 are really there - the project
 * carries a TrackingScope of 2 nodeKeys that excludes every conduit. Without
 * this flag the first thing anybody filters for reads as an empty model.
 */
export function toFilterBody(rules, gate, { scope = true } = {}) {
  return {
    // The endpoint is SCOPED BY DEFAULT (`b.scope !== false`), so scope:true is
    // expressed by sending nothing. ONE caller decides this and passes it to
    // both calls, so the applied filter and the comparison can never disagree
    // about which question was asked.
    ...(scope ? {} : { scope: false }),
    filters: {
      gate,
      rules: rules.map((r) => {
        const out = { field: r.field, op: r.op };
        if (!VALUELESS_OPS.includes(r.op)) {
          out.value = fieldKind(r.field) === 'number' ? Number(r.value) : r.value;
        }
        if (r.gate) out.gate = r.gate;
        return out;
      }),
    },
  };
}

/**
 * What the server's answer means, in one sentence, INCLUDING the ways it is
 * not a list of ids. Every refusal keeps its own wording: "too many" and
 * "timed out" and "nothing matched" are three different facts and a filter
 * bar that renders them all as an empty model is lying about two of them.
 */
export function readAnswer(res) {
  if (!res) return { kind: 'error', message: 'No answer from the server' };
  if (res.refused || res.tooMany) {
    const n = typeof res.count === 'number' ? res.count.toLocaleString() : null;
    if (res.reason === 'timeout' || (res.refused && res.count === null)) {
      return { kind: 'refused', message: 'The model took too long to answer - the view was left as it was' };
    }
    return {
      kind: 'refused',
      message: n
        ? `${n} elements match - too many to show at once. Narrow the rules.`
        : 'Too many elements match to show at once. Narrow the rules.',
    };
  }
  if (res.reason === 'no live model') {
    return { kind: 'refused', message: 'This project has no live model to filter' };
  }
  if (!res.count) return { kind: 'empty', message: 'Nothing matched these rules', count: 0 };
  return { kind: 'ok', count: res.count, message: `${res.count.toLocaleString()} elements matched` };
}


/**
 * THE ONLY WAY TO GET A UID SET, AND IT CANNOT HAND ONE BACK WITHOUT THE
 * SENTENCE THAT EXPLAINS IT.
 *
 * The owner rejected a scoped filter that silently hid elements he could see,
 * then approved the scoped filter WITH the count that says so. The count is not
 * a detail of that decision - it IS the difference between the two. So the
 * isolate set and the message are produced by one function and returned
 * together: there is no code path that yields `uids` with no `message`, which
 * means the rejected behaviour is unreachable rather than merely discouraged.
 *
 * TWO CALLS, because one cannot answer it. filter-uids counts what its own match
 * found, so the scoped count and the whole-model count are two questions. The
 * scoped answer is what we APPLY; the unscoped answer exists only to say how
 * much is outside.
 *
 * AND THE UNSCOPED CALL IS THE ONE THAT CAN REFUSE - it asks about the whole
 * model and filter-uids refuses above its limit rather than truncating. So on a
 * large project the scoped call can succeed while the comparison does not. In
 * that case we say "more than <limit> outside", NEVER a guessed number and
 * NEVER a message with the comparison quietly dropped - an omitted sentence is
 * the build he rejected, arriving by another route.
 *
 * `fetchUids` and `expand` are injected so this is testable without a network.
 */
export async function resolveFilter({ fetchUids, expand, projectId, rules, gate }) {
  const problem = rulesProblem(rules);
  if (problem) return { kind: 'invalid', message: problem, uids: null };

  const scopedRes = await fetchUids(projectId, toFilterBody(rules, gate));
  const scoped = readAnswer(scopedRes);
  if (scoped.kind === 'refused' || scoped.kind === 'error') {
    return { kind: scoped.kind, message: scoped.message, uids: null };
  }
  const inside = scoped.count || 0;

  // The comparison. Its failure is expected and must be SAID, not swallowed.
  let outside = null;
  let outsideAtLeast = null;
  try {
    const wholeRes = await fetchUids(projectId, toFilterBody(rules, gate, { scope: false }));
    if (wholeRes && (wholeRes.tooMany || wholeRes.refused)) {
      outsideAtLeast = typeof wholeRes.limit === 'number' ? wholeRes.limit : null;
    } else {
      const whole = readAnswer(wholeRes);
      if (whole.kind === 'ok' || whole.kind === 'empty') {
        outside = Math.max(0, (wholeRes.count || 0) - inside);
      } else {
        outsideAtLeast = -1;   // refused without a limit to quote
      }
    }
  } catch (e) {
    outsideAtLeast = -1;
  }

  const outsideText = outside !== null
    ? (outside === 0 ? null : `${outside.toLocaleString()} more match outside the tracked scope and are not shown`)
    : (outsideAtLeast > 0
      ? `more than ${outsideAtLeast.toLocaleString()} match outside the tracked scope and are not shown`
      : 'the number outside the tracked scope could not be counted');

  // 0 INSIDE IS THE CASE THAT LOOKS BROKEN, so it gets the fullest sentence.
  if (inside === 0) {
    return {
      kind: 'empty',
      message: outsideText
        ? `Nothing matched inside the tracked scope - ${outsideText}`
        : 'Nothing matched these rules',
      uids: null,
    };
  }

  const uids = [...expand(scopedRes)];
  return {
    kind: 'ok',
    message: `${inside.toLocaleString()} matched inside the tracked scope`
      + (outsideText ? `; ${outsideText}` : ''),
    uids,
  };
}
